import { useLingui } from "@lingui/react/macro";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@rakazo/ui-web";

export type LiveUnavailableReason =
  | "no_key"
  | "no_transcriber"
  | "disabled"
  | "unavailable"
  | "error"
  | "permission";

/**
 * Explains why live conversation cannot start. Kept as a small modal instead of
 * persistent composer chrome; the setup action goes to Settings → Models.
 */
export function LiveUnavailableNotice({
  reason,
  onClose,
  onOpenModels,
}: {
  reason: LiveUnavailableReason;
  onClose: () => void;
  onOpenModels?: () => void;
}) {
  const { t } = useLingui();
  const title =
    reason === "no_key"
      ? t`Add a Google API key`
      : reason === "no_transcriber"
        ? t`Connect speech recognition`
        : reason === "disabled"
          ? t`Live conversation is off`
          : reason === "permission"
            ? t`Microphone access is blocked`
            : reason === "error"
              ? t`Live conversation couldn't start`
              : t`Live conversation isn't available`;
  const message =
    reason === "no_key"
      ? t`Add your Google API key in the bot settings to use live conversation.`
      : reason === "no_transcriber"
        ? t`Add a transcription connection in Voice settings to deliver spoken requests to your bot.`
        : reason === "disabled"
          ? t`Live conversation is turned off on this server.`
          : reason === "permission"
            ? t`Allow the microphone in your system or browser settings, then try again.`
            : reason === "error"
              ? t`Try again, or keep using text.`
              : t`Live conversation isn't available on this server.`;

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        className="app-no-drag w-[calc(100vw-2rem)] max-w-sm rounded-3xl"
        showCloseButton={false}
      >
        <DialogHeader>
          <DialogTitle className="leading-snug">{title}</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          {(reason === "no_key" || reason === "no_transcriber") && onOpenModels ? (
            <Button className="min-h-11" onClick={onOpenModels}>
              {t`Open settings`}
            </Button>
          ) : null}
          <Button variant="outline" className="min-h-11" onClick={onClose}>
            {t`Close`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
